import { useEffect, useMemo, useState } from 'react'
import type {
  FormFieldUpdatePayload,
  FormTemplateField,
  FormInspectionFrequency,
} from '@/types'
import { FormFieldType, FormType } from '@/types'
import { TrashIcon } from '@heroicons/react/24/outline'
import {
  supportsOptions,
  generateOptionValue,
  processOptions,
  buildOptionsFromText,
  createOptionItem,
  ensureOptionId,
  type OptionItem,
} from './optionUtils'
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface FieldInspectorProps {
  field?: FormTemplateField
  onUpdateField: (fieldId: string, payload: FormFieldUpdatePayload) => Promise<void>
  onDeleteField: (fieldId: string) => Promise<void>
  availableCategories?: string[]
  availableSubcategories?: Record<string, string[]>
  formType?: FormType
}

const fallbackOptionLabel = '選項 1'

const FREQUENCY_OPTIONS: Array<{ value: FormInspectionFrequency; label: string }> = [
  { value: 'NONE', label: '不指定' },
  { value: 'DAILY', label: '每日' },
  { value: 'WEEKLY', label: '每週（週一）' },
  { value: 'MONTHLY', label: '每月（1 日）' },
  { value: 'QUARTERLY', label: '每季（1 日）' },
]

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) {
    return error.message
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.length > 0) {
      return message
    }
  }
  return fallback
}

export const FieldInspector = ({
  field,
  onUpdateField,
  onDeleteField,
  availableCategories = [],
  availableSubcategories = {},
  formType = FormType.PERIODIC,
}: FieldInspectorProps) => {
  const [formState, setFormState] = useState({
    label: '',
    description: '',
    isRequired: false,
    isRepeatable: false,
    helpText: '',
    placeholder: '',
    category: '',
    subcategory: '',
    type: FormFieldType.SHORT_TEXT,
  })
  const [options, setOptions] = useState<OptionItem[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [optionError, setOptionError] = useState<string | null>(null)
  const [bulkEditorOpen, setBulkEditorOpen] = useState(false)
  const [bulkEditorValue, setBulkEditorValue] = useState('')
  const [bulkEditorError, setBulkEditorError] = useState<string | null>(null)
  const [inspectionFrequency, setInspectionFrequency] = useState<FormInspectionFrequency>('NONE')

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const isPeriodicTemplate = formType === FormType.PERIODIC

  useEffect(() => {
    if (!field) {
      setError(null)
      setOptionError(null)
      setBulkEditorValue('')
      return
    }

    setFormState({
      label: field.label,
      description: field.description ?? '',
      isRequired: Boolean(field.isRequired),
      isRepeatable: Boolean(field.isRepeatable),
      helpText: field.helpText ?? '',
      placeholder: field.placeholder ?? '',
      category: field.category ?? '',
      subcategory: field.subcategory ?? '',
      type: field.type,
    })

    if (isPeriodicTemplate) {
      const frequency =
        (field.metadata?.inspectionFrequency as FormInspectionFrequency | undefined) ?? 'NONE'
      setInspectionFrequency(
        FREQUENCY_OPTIONS.some(option => option.value === frequency) ? frequency : 'NONE',
      )
    } else {
      setInspectionFrequency('NONE')
    }

    if (supportsOptions(field.type)) {
      const incomingOptions: OptionItem[] = (field.options || []).map((option, index) =>
        createOptionItem(option.label, option.value || generateOptionValue(option.label, index)),
      )

      if (incomingOptions.length === 0) {
        const defaultLabel = fallbackOptionLabel
        setOptions([createOptionItem(defaultLabel, generateOptionValue(defaultLabel, 0))])
      } else {
        setOptions(incomingOptions.map(ensureOptionId))
      }
    } else {
      setOptions([])
    }

    setError(null)
    setOptionError(null)
    setBulkEditorValue('')
  }, [field, isPeriodicTemplate])

  const optionIds = useMemo(() => options.map(option => option.id), [options])

  const categoryListId = useMemo(() => `field-category-options-${field?.id ?? 'new'}`, [field?.id])
  const subcategoryListId = useMemo(
    () => `field-subcategory-options-${field?.id ?? 'new'}`,
    [field?.id],
  )

  const categorySuggestions = useMemo(() => {
    return Array.from(new Set(availableCategories.filter(Boolean))).sort((a, b) =>
      a.localeCompare(b, 'zh-Hant-TW', { numeric: true }),
    )
  }, [availableCategories])

  const subcategorySuggestions = useMemo(() => {
    if (!formState.category) return [] as string[]
    const suggestions = availableSubcategories[formState.category] ?? []
    return Array.from(new Set(suggestions.filter(Boolean))).sort((a, b) =>
      a.localeCompare(b, 'zh-Hant-TW', { numeric: true }),
    )
  }, [availableSubcategories, formState.category])

  const handleChange = (
    key: keyof typeof formState,
    value: string | boolean | FormFieldType,
  ) => {
    if (key === 'category') {
      const nextCategory = String(value)
      setFormState(prev => {
        const candidateSubcategories = nextCategory
          ? availableSubcategories[nextCategory] ?? []
          : []
        const preservedSubcategory = candidateSubcategories.includes(prev.subcategory)
          ? prev.subcategory
          : ''
        return {
          ...prev,
          category: nextCategory,
          subcategory: preservedSubcategory,
        }
      })
      return
    }

    if (key === 'subcategory') {
      setFormState(prev => ({ ...prev, subcategory: String(value) }))
      return
    }

    setFormState(prev => ({ ...prev, [key]: value }))

    if (key === 'type') {
      const nextType = value as FormFieldType
      if (!supportsOptions(nextType)) {
        setOptions([])
        setOptionError(null)
      } else if (supportsOptions(nextType) && options.length === 0) {
        const defaultLabel = fallbackOptionLabel
        setOptions([createOptionItem(defaultLabel, generateOptionValue(defaultLabel, 0))])
      }
    }
  }

  const handleAddOption = () => {
    const nextIndex = options.length
    const label = `選項 ${nextIndex + 1}`
    const value = generateOptionValue(label, nextIndex)
    setOptions(prev => [...prev, createOptionItem(label, value)])
    setOptionError(null)
  }

  const handleOptionChange = (index: number, key: 'label' | 'value', value: string) => {
    setOptions(prev => prev.map((option, idx) => (idx === index ? { ...option, [key]: value } : option)))
    setOptionError(null)
  }

  const handleOptionDelete = (index: number) => {
    setOptions(prev => prev.filter((_, idx) => idx !== index))
    setOptionError(null)
  }

  const handleOptionDuplicate = (index: number) => {
    setOptions(prev => {
      const source = prev[index]
      if (!source) return prev
      const baseLabel = source.label.trim() || `選項 ${index + 1}`
      const duplicateLabel = `${baseLabel} (複製)`
      const nextIndex = prev.length
      const inserted = createOptionItem(duplicateLabel, generateOptionValue(duplicateLabel, nextIndex))
      const copy = [...prev]
      copy.splice(index + 1, 0, inserted)
      return copy
    })
    setOptionError(null)
  }

  const handleOptionRegenerateValue = (index: number) => {
    setOptions(prev =>
      prev.map((option, idx) =>
        idx === index
          ? {
              ...option,
              value: generateOptionValue(option.label || `選項 ${idx + 1}`, idx),
            }
          : option,
      ),
    )
    setOptionError(null)
  }

  const handleRegenerateAllValues = () => {
    setOptions(prev =>
      prev.map((option, index) => ({
        ...option,
        value: generateOptionValue(option.label || `選項 ${index + 1}`, index),
      })),
    )
    setOptionError(null)
  }

  const handleOptionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = optionIds.indexOf(active.id as string)
    const newIndex = optionIds.indexOf(over.id as string)
    if (oldIndex === -1 || newIndex === -1) {
      return
    }

    setOptions(prev => arrayMove(prev, oldIndex, newIndex))
    setOptionError(null)
  }

  const handleBulkApply = () => {
    const parsed = buildOptionsFromText(bulkEditorValue, options.length)
    if (parsed.length === 0) {
      setBulkEditorError('請輸入至少一行文字')
      return
    }

    setOptions(prev => [...prev, ...parsed])
    setBulkEditorValue('')
    setBulkEditorError(null)
    setBulkEditorOpen(false)
    setOptionError(null)
  }

  const handleBulkClose = () => {
    setBulkEditorOpen(false)
    setBulkEditorValue('')
    setBulkEditorError(null)
  }

  const handleClearOptions = () => {
    if (!window.confirm('確定要清空所有選項嗎？')) {
      return
    }
    setOptions([])
    setOptionError(null)
  }

  const { sanitized: sanitizedOptions, error: optionValidationError } = useMemo(() => {
    if (!supportsOptions(formState.type)) {
      return { sanitized: [] as OptionItem[], error: null as string | null }
    }
    return processOptions(options)
  }, [options, formState.type])

  const effectiveOptionError = optionError || optionValidationError

  const handleSave = async () => {
    if (!field) return
    try {
      setIsSaving(true)

      const shouldSendOptions = supportsOptions(formState.type)
      if (shouldSendOptions && optionValidationError) {
        setOptionError(optionValidationError)
        setIsSaving(false)
        return
      }

      const nextMetadata: Record<string, unknown> = {
        ...(field.metadata ?? {}),
      }

      if (isPeriodicTemplate) {
        if (inspectionFrequency === 'NONE') {
          delete nextMetadata.inspectionFrequency
        } else {
          nextMetadata.inspectionFrequency = inspectionFrequency
        }
      } else {
        delete nextMetadata.inspectionFrequency
      }

      if (Object.keys(nextMetadata).length === 0) {
        delete nextMetadata.inspectionFrequency
      }

      const metadataPayload = Object.keys(nextMetadata).length > 0 ? nextMetadata : undefined

      await onUpdateField(field.id, {
        label: formState.label,
        description: formState.description,
        isRequired: formState.isRequired,
        isRepeatable: formState.isRepeatable,
        helpText: formState.helpText,
        placeholder: formState.placeholder,
        category: formState.category || null,
        subcategory: formState.subcategory || null,
        type: formState.type,
        metadata: metadataPayload,
        options: shouldSendOptions
          ? sanitizedOptions.map((option, index) => ({
              label: option.label,
              value: option.value,
              position: index,
            }))
          : [],
      })
      setError(null)
      setOptionError(null)
    } catch (err: unknown) {
      setError(getErrorMessage(err, '儲存欄位設定時發生錯誤'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!field) return
    if (!window.confirm(`確定要刪除欄位「${field.label}」嗎？`)) {
      return
    }
    try {
      setIsSaving(true)
      await onDeleteField(field.id)
      setError(null)
    } catch (err: unknown) {
      setError(getErrorMessage(err, '刪除欄位失敗'))
    } finally {
      setIsSaving(false)
    }
  }

  if (!field) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500">
        請選擇欄位以檢視屬性
      </div>
    )
  }

  const optionEditorVisible = supportsOptions(formState.type)

  return (
    <div className="flex-1 min-h-0 rounded-lg border border-gray-200 bg-white flex flex-col overflow-hidden max-h-[calc(100vh-160px)]">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">欄位屬性</h3>
          <p className="text-xs text-gray-500">{field.type}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSaving ? '儲存中...' : '儲存設定'}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="inline-flex items-center rounded-md border border-transparent bg-red-50 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-100"
            disabled={isSaving}
          >
            <TrashIcon className="h-4 w-4 mr-1" /> 刪除
          </button>
        </div>
      </div>

      {error && (
        <p className="px-4 text-xs text-red-600">{error}</p>
      )}

      <div className="space-y-4 flex-1 overflow-y-auto px-4 pb-8">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">欄位類型</label>
          <select
            value={formState.type}
            onChange={(e) => handleChange('type', e.target.value as FormFieldType)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
          >
            {Object.values(FormFieldType).map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">顯示名稱</label>
          <input
            type="text"
            value={formState.label}
            onChange={(e) => handleChange('label', e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">描述</label>
          <textarea
            value={formState.description}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={3}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <div className="flex items-center space-x-2">
          <input
            id="field-required"
            type="checkbox"
            checked={formState.isRequired}
            onChange={(e) => handleChange('isRequired', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="field-required" className="text-sm text-gray-600">必填欄位</label>
        </div>

        <div className="flex items-center space-x-2">
          <input
            id="field-repeatable"
            type="checkbox"
            checked={formState.isRepeatable}
            onChange={(e) => handleChange('isRepeatable', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="field-repeatable" className="text-sm text-gray-600">允許重複輸入</label>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">提示文字</label>
          <input
            type="text"
            value={formState.helpText}
            onChange={(e) => handleChange('helpText', e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">預設占位文字</label>
          <input
            type="text"
            value={formState.placeholder}
            onChange={(e) => handleChange('placeholder', e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">分類</label>
            <input
              type="text"
              list={categoryListId}
              value={formState.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
              />
            <datalist id={categoryListId}>
              {categorySuggestions.map(category => (
                <option key={category} value={category} />
              ))}
            </datalist>
            {categorySuggestions.length > 0 && (
              <p className="mt-1 text-[11px] text-gray-400">
                可輸入新分類，或選擇既有分類快速套用
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">子分類</label>
            <input
              type="text"
              list={subcategoryListId}
              value={formState.subcategory}
              onChange={(e) => handleChange('subcategory', e.target.value)}
              disabled={!formState.category}
              placeholder={formState.category ? '輸入或選擇子分類' : '請先選擇分類'}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
            />
            <datalist id={subcategoryListId}>
              {subcategorySuggestions.map(subcategory => (
                <option key={subcategory} value={subcategory} />
              ))}
            </datalist>
            {formState.category && subcategorySuggestions.length > 0 && (
              <p className="mt-1 text-[11px] text-gray-400">
                已有子分類：{subcategorySuggestions.join('、')}
              </p>
            )}
          </div>
        </div>

        {isPeriodicTemplate && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">檢查頻率</label>
            <select
              value={inspectionFrequency}
              onChange={(e) => setInspectionFrequency(e.target.value as FormInspectionFrequency)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
            >
              {FREQUENCY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              設定此欄位是否需要每日、每週或每月定期檢查。系統會依據頻率建立提醒 Issue，請於專案表單的「欄位負責人」設定指派對象。
            </p>
          </div>
        )}

        {optionEditorVisible && (
          <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-600">選項</span>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-gray-500">共 {options.length} 項</span>
                <button
                  type="button"
                  onClick={() => setBulkEditorOpen(true)}
                  className="rounded border border-gray-300 px-2 py-1 text-[11px] text-gray-600 hover:bg-white"
                >
                  批次新增
                </button>
                <button
                  type="button"
                  onClick={handleRegenerateAllValues}
                  className="rounded border border-gray-300 px-2 py-1 text-[11px] text-gray-600 hover:bg-white"
                >
                  重新產生值
                </button>
                <button
                  type="button"
                  onClick={handleAddOption}
                  className="rounded bg-primary-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-primary-700"
                >
                  新增選項
                </button>
                <button
                  type="button"
                  onClick={handleClearOptions}
                  className="rounded border border-red-200 px-2 py-1 text-[11px] text-red-600 hover:bg-red-50"
                >
                  清空
                </button>
              </div>
            </div>
            {options.length === 0 ? (
              <p className="text-xs text-gray-500">尚未設定選項</p>
            ) : (
              <DndContext sensors={sensors} onDragEnd={handleOptionDragEnd}>
                <SortableContext items={optionIds} strategy={verticalListSortingStrategy}>
                  <ul className="space-y-2">
                    {options.map((option, index) => (
                      <OptionListItem
                        key={optionIds[index]}
                        id={optionIds[index]}
                        index={index}
                        option={option}
                        onChange={handleOptionChange}
                        onDelete={handleOptionDelete}
                        onDuplicate={handleOptionDuplicate}
                        onRegenerateValue={handleOptionRegenerateValue}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
            {effectiveOptionError && (
              <p className="text-xs text-red-600">{effectiveOptionError}</p>
            )}
          </div>
        )}

        {error && (
          <p className="text-xs text-red-600">{error}</p>
        )}

        <div className="h-12" />
      </div>

      {bulkEditorOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
            <h4 className="text-base font-semibold text-gray-800">批次新增選項</h4>
            <p className="mt-1 text-xs text-gray-500">每行輸入一個選項，系統會自動產生值並附加到清單後方。</p>
            <textarea
              value={bulkEditorValue}
              onChange={(e) => {
                setBulkEditorValue(e.target.value)
                setBulkEditorError(null)
              }}
              rows={8}
              className="mt-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
            />
            {bulkEditorError && (
              <p className="mt-1 text-xs text-red-600">{bulkEditorError}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={handleBulkClose}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleBulkApply}
                className="rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
              >
                匯入
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

interface OptionListItemProps {
  id: string
  index: number
  option: OptionItem
  onChange: (index: number, key: 'label' | 'value', value: string) => void
  onDelete: (index: number) => void
  onDuplicate: (index: number) => void
  onRegenerateValue: (index: number) => void
}

const OptionListItem = ({
  id,
  index,
  option,
  onChange,
  onDelete,
  onDuplicate,
  onRegenerateValue,
}: OptionListItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`w-full rounded-md border border-gray-200 bg-white p-2 shadow-sm ${isDragging ? 'ring-2 ring-primary-200' : ''}`}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <button
          type="button"
          className="flex h-6 w-6 cursor-grab items-center justify-center rounded border border-gray-200 bg-gray-50 text-gray-500"
          {...attributes}
          {...listeners}
        >
          <span className="text-xs">↕</span>
        </button>
        <span className="text-[11px] text-gray-400">#{index + 1}</span>
        <input
          type="text"
          value={option.label}
          onChange={(e) => onChange(index, 'label', e.target.value)}
          className="min-w-0 flex-1 rounded border border-gray-200 px-2 py-1 text-xs focus:border-primary-500 focus:ring-primary-500"
          placeholder="顯示文字"
        />
        <input
          type="text"
          value={option.value}
          onChange={(e) => onChange(index, 'value', e.target.value)}
          className="w-32 rounded border border-gray-200 px-2 py-1 text-xs focus:border-primary-500 focus:ring-primary-500"
          placeholder="值"
        />
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onDuplicate(index)}
            className="rounded border border-gray-200 px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50"
          >
            複製
          </button>
          <button
            type="button"
            onClick={() => onRegenerateValue(index)}
            className="rounded border border-gray-200 px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50"
          >
            產生值
          </button>
          <button
            type="button"
            onClick={() => onDelete(index)}
            className="rounded border border-red-200 px-1.5 py-0.5 text-[11px] text-red-600 hover:bg-red-50"
          >
            刪除
          </button>
        </div>
      </div>
    </li>
  )
}

export default FieldInspector
